/**
 * patrol-building-search-edit.js
 */

import React from 'react';
import { Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Types } from 'utils/constants';
import { EditorIconSection } from 'components/icon-section/icon-section';
import HelpTextItem from 'components/menu-drawer/help-text-item';
import FormGridItem from 'components/menu-drawer/form-grid-item';
import EditorAccordion from 'components/menu-drawer/editor-accordion';
import HiddenInput from 'components/form-inputs/hidden-input';
import CheckboxList from 'components/form-inputs/checkbox-list';
import TextField from 'components/form-inputs/text-field';
import Select from 'components/form-inputs/select';
import PatrolArrestsBitesEdit from '../../helpers/patrol-arrests-bites-edit';


export default function PatrolBuildingSearchEdit(props) {
  const {
    drawerState,
    edit,
    patrolIndex,
    isAdd
  } = props;
  const { t } = useTranslation(edit.translation);


  return (
    <>
      {/* patrol-type id */}
      <HiddenInput 
        name={`patrol.patrolTypes[${patrolIndex}].id`} 
        defaultValue={isAdd ? '' : undefined}
      />

      {/* type */}
      <HiddenInput 
        name={`patrol.patrolTypes[${patrolIndex}].type`}
        defaultValue={isAdd ? Types.Patrol.BUILDING_SEARCH : undefined}
      />

      {/* order */}
      <HiddenInput 
        name={`patrol.patrolTypes[${patrolIndex}].order`} 
        defaultValue={isAdd ? patrolIndex + 1 : undefined}
      />

      {/* building-search id */}
      <HiddenInput 
        name={`patrol.patrolTypes[${patrolIndex}].buildingSearch.id`} 
        defaultValue={isAdd ? '' : undefined}
      />


      <EditorIconSection
        iconName='patrol-building-search'
        iconMarginTop={6}
        helpProps={{
          translation: edit.translation,
          section: 'buildingSearch'
        }}
      >
        
        {/* building search types */}
        <HelpTextItem
          sidebarText={drawerState.sidebarText}
          title={t('buildingSearch.types.helpTitle')}
          text={t('buildingSearch.types.helpInfo')}
        >
          {focusHandlers =>
            <CheckboxList
              name={`patrol.patrolTypes[${patrolIndex}].buildingSearch.types`}
              label={t('buildingSearch.types.label')}
              dataTestId='building-search-types'
              options={edit.options.buildingSearchTypes}
              customOther
              focusHandlers={focusHandlers}
              rules={{
                validate: val => (val && val.length > 0) || t('buildingSearch.types.errMsgEmpty')
              }}
            />}
        </HelpTextItem>


        {/* search duration */}
        <HelpTextItem
          sidebarText={drawerState.sidebarText}
          title={t('buildingSearch.searchDuration.helpTitle')}
          text={t('buildingSearch.searchDuration.helpInfo')}
        >
          {focusHandlers =>
            <div className='flex-hc'>
              <TextField
                name={`patrol.patrolTypes[${patrolIndex}].buildingSearch.searchDurationMinutes`}
                label={t('buildingSearch.searchDuration.label')}
                dataTestId='building-search-search-duration-minutes'
                defaultValue={isAdd ? '' : undefined}
                type='number'
                focusHandlers={focusHandlers}
                style={{ maxWidth: 195 }}
                rules={{
                  required: t('buildingSearch.searchDuration.errMsgEmpty'),
                  max: {
                    value: 999999999,
                    message: t('buildingSearch.searchDuration.errMsgSize')
                  },
                }}
              />
            </div>}
        </HelpTextItem>


        {/* OPTIONAL */}
        <EditorAccordion
          name={`${t('buildingSearch.sectionName')} Optional`}
          data-testid='optional-area-size-accordion'

          summary={
            <Typography variant='body2'>
              OPTIONAL: 
              {` ${t('buildingSearch.trackAge.label')},`}
              {` ${t('buildingSearch.areaSize.label')}`}
            </Typography>}

          detail={<>

            {/* track age / time delay */}
            <HelpTextItem
              sidebarText={drawerState.sidebarText}
              title={t('buildingSearch.trackAge.helpTitle')}
              text={t('buildingSearch.trackAge.helpInfo')}
            >
              {focusHandlers =>
                <div className='flex-hc'>
                  <TextField
                    name={`patrol.patrolTypes[${patrolIndex}].buildingSearch.trackAgeMinutes`}
                    label={t('buildingSearch.trackAge.label')}
                    dataTestId='building-search-track-age-minutes'
                    defaultValue={isAdd ? '' : undefined}
                    type='number'
                    focusHandlers={focusHandlers}
                    style={{ maxWidth: 195 }}
                    rules={{
                      max: {
                        value: 999999999,
                        message: t('buildingSearch.trackAge.errMsgSize')
                      },
                    }}
                  />
                </div>}
            </HelpTextItem>


            {/* area size */}
            <HelpTextItem
              sidebarText={drawerState.sidebarText}
              title={t('buildingSearch.areaSize.helpTitle')}
              text={t('buildingSearch.areaSize.helpInfo')}
            >
              {focusHandlers =>
                <Grid container spacing={2}>
                  <FormGridItem xs={5} smd={6} smm={3} sm={3} lg={3}> 
                    <TextField
                      name={`patrol.patrolTypes[${patrolIndex}].buildingSearch.areaSize`}
                      label={t('buildingSearch.areaSize.label')}
                      dataTestId='building-search-area-size'
                      defaultValue={isAdd ? '' : undefined}
                      type='number'
                      focusHandlers={focusHandlers}
                    />
                  </FormGridItem>
                  <FormGridItem xs={7} smd={6} smm={3} sm={4} lg={4}>
                    <Select
                      name={`patrol.patrolTypes[${patrolIndex}].buildingSearch.areaSizeUnit`}
                      dataTestId='building-search-area-size-unit'
                      options={edit.options.indoorAreaUnits}
                      defaultValue={isAdd ? edit.options.indoorAreaUnits[0] : undefined}
                      focusHandlers={focusHandlers}
                      style={{ marginTop: 4 }}
                      setValueAs={id => ({ id })}
                    />
                  </FormGridItem>
                </Grid>}
            </HelpTextItem>

          </>}
        />

      </EditorIconSection>


      {/* arrests / bites */}
      <PatrolArrestsBitesEdit {...props} />
    </>
  );
}